import React, { useState, useCallback, ChangeEvent, useEffect } from 'react';
import { Input } from 'antd';
import { debounce } from 'lodash';

const { Search } = Input;

interface DebounceSearchProps {
  placeholder: string;
  onSearch: (value: string) => void;
  className?: string;
  disabled?: boolean;
  allowClear?: boolean;
  size?: 'small' | 'middle' | 'large';
  timeout?: number;
  initialValue?: string;
}

const DebounceSearch: React.FC<DebounceSearchProps> = ({
  placeholder,
  onSearch,
  className,
  disabled = false,
  allowClear = true,
  size = 'middle',
  timeout = 300,
  initialValue = '',
}) => {
  const [value, setValue] = useState<string>(initialValue);

  // Update local state when initialValue changes
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      onSearch(searchValue);
    }, timeout),
    [onSearch]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue); // Local state - no parent re-render
      debouncedSearch(newValue);
    },
    [debouncedSearch]
  );

  const handleSearch = useCallback(
    (searchValue: string) => {
      setValue(searchValue);
      onSearch(searchValue);
    },
    [onSearch]
  );

  return (
    <Search
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onSearch={handleSearch}
      className={className}
      disabled={disabled}
      allowClear={allowClear}
      size={size}
    />
  );
};

export default DebounceSearch;
